import {
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Link,
  Undo2,
  Redo2,
  Mail,
  Loader2,
} from 'lucide-react'
import { toast } from '../../lib/shadcn/sonner'
import { useSendTrackingEmail } from '../../hooks/backend/scanPage'

interface TrackingEmailDialogProps {
  orderId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface PreviewResult {
  to: string
  recipient: string | null
  subject: string
  body: string
}

function isAbortError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : String(error)

  return message.toLowerCase().includes('aborted')
}

function wait(ms: number): Promise<void> {
  return new Promise(resolve => {
    window.setTimeout(resolve, ms)
  })
}

export default function TrackingEmailDialog({
  orderId,
  open,
  onOpenChange,
}: TrackingEmailDialogProps) {
  const trackingEmail = useSendTrackingEmail()

  const loadIdRef = useRef(0)
  const editorRef = useRef<HTMLDivElement | null>(null)

  const [to, setTo] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open || !orderId) {
      loadIdRef.current += 1
      return
    }

    const loadId = ++loadIdRef.current

    const loadPreview = async () => {
      setLoading(true)

      let lastError: unknown = null

      try {
        for (let attempt = 0; attempt < 2; attempt += 1) {
          try {
            const res =
  (await trackingEmail.trigger({
    order_id: orderId,
    preview: true,
  }).result) as PreviewResult

            if (loadId !== loadIdRef.current) {
              return
            }

            setTo(res?.to ?? '')
            setSubject(res?.subject ?? '')
            setBody(res?.body ?? '')

            return
          } catch (error) {
            lastError = error

            if (loadId !== loadIdRef.current) {
              return
            }

            if (!isAbortError(error)) {
              throw error
            }

            if (attempt === 0) {
              await wait(250)
            }
          }
        }

        throw lastError
      } catch (error) {
        if (loadId !== loadIdRef.current) {
          return
        }

        const message =
          error instanceof Error
            ? error.message
            : 'Unknown error'

        toast.error(
          `Could not load email: ${message}`,
        )
      } finally {
        if (loadId === loadIdRef.current) {
          setLoading(false)
        }
      }
    }

    void loadPreview()

    return () => {
      if (loadId === loadIdRef.current) {
        loadIdRef.current += 1
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orderId])

  /*
   * When the backend email loads, copy its HTML into
   * the WYSIWYG editor.
   */
  useEffect(() => {
    if (!editorRef.current) return

    if (editorRef.current.innerHTML !== body) {
      editorRef.current.innerHTML = body
    }
  }, [body, loading])

  const syncEditorBody = () => {
    if (!editorRef.current) return

    setBody(editorRef.current.innerHTML)
  }

  const runCommand = (
    command: string,
    value?: string,
  ) => {
    editorRef.current?.focus()

    document.execCommand(
      command,
      false,
      value,
    )

    syncEditorBody()
  }

  const handleLink = () => {
    const url = window.prompt(
      'Enter the link URL:',
      'https://',
    )

    if (!url) return

    runCommand('createLink', url)
  }

  const handleSend = async () => {
  if (!orderId) return

  if (!to.trim()) {
    toast.error('Enter a recipient email address')
    return
  }

  const currentBody =
    editorRef.current?.innerHTML ?? body

  setBody(currentBody)

  try {
    await trackingEmail.trigger({
  order_id: orderId,
  to: to.trim(),
  subject,
  body: currentBody,
}).result

    toast.success(
      `Tracking email sent to ${to.trim()}`,
    )

    onOpenChange(false)
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : 'Unknown error'

    if (isAbortError(error)) {
      toast.warning(
        'The send request was interrupted. Check your Sent folder before trying again to avoid sending the email twice.',
      )
      return
    }

    toast.error(`Failed to send: ${message}`)
  }
}

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Email tracking to customer
          </DialogTitle>

          <DialogDescription>
            {orderId
              ? `Order ${orderId}`
              : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto pr-2">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Preparing email…
            </div>
          ) : (
            <div className="space-y-4 py-1">
              <div className="space-y-1.5">
                <Label htmlFor="tracking-to">
                  To
                </Label>

                <Input
                  id="tracking-to"
                  type="email"
                  value={to}
                  onChange={e =>
                    setTo(e.target.value)
                  }
                  placeholder="customer@example.com"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="tracking-subject">
                  Subject
                </Label>

                <Input
                  id="tracking-subject"
                  value={subject}
                  onChange={e =>
                    setSubject(e.target.value)
                  }
                />
              </div>

              <div className="space-y-1.5">
                <Label>
                  Message
                </Label>

                <div className="overflow-hidden rounded-md border bg-background">
                  {/* WYSIWYG toolbar */}
                  <div className="flex flex-wrap items-center gap-1 border-b bg-muted/30 p-1.5">
                    <EditorButton
                      title="Bold"
                      onClick={() =>
                        runCommand('bold')
                      }
                    >
                      <Bold className="h-4 w-4" />
                    </EditorButton>

                    <EditorButton
                      title="Italic"
                      onClick={() =>
                        runCommand('italic')
                      }
                    >
                      <Italic className="h-4 w-4" />
                    </EditorButton>

                    <EditorButton
                      title="Underline"
                      onClick={() =>
                        runCommand('underline')
                      }
                    >
                      <Underline className="h-4 w-4" />
                    </EditorButton>

                    <div className="mx-1 h-5 w-px bg-border" />

                    <EditorButton
                      title="Bulleted list"
                      onClick={() =>
                        runCommand(
                          'insertUnorderedList',
                        )
                      }
                    >
                      <List className="h-4 w-4" />
                    </EditorButton>

                    <EditorButton
                      title="Numbered list"
                      onClick={() =>
                        runCommand(
                          'insertOrderedList',
                        )
                      }
                    >
                      <ListOrdered className="h-4 w-4" />
                    </EditorButton>

                    <div className="mx-1 h-5 w-px bg-border" />

                    <EditorButton
                      title="Insert link"
                      onClick={handleLink}
                    >
                      <Link className="h-4 w-4" />
                    </EditorButton>

                    <div className="mx-1 h-5 w-px bg-border" />

                    <EditorButton
                      title="Undo"
                      onClick={() =>
                        runCommand('undo')
                      }
                    >
                      <Undo2 className="h-4 w-4" />
                    </EditorButton>

                    <EditorButton
                      title="Redo"
                      onClick={() =>
                        runCommand('redo')
                      }
                    >
                      <Redo2 className="h-4 w-4" />
                    </EditorButton>
                  </div>

                  {/* WYSIWYG editing area */}
                  <div
                    ref={editorRef}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={syncEditorBody}
                    className="
                      min-h-[300px]
                      max-h-[450px]
                      overflow-y-auto
                      bg-white
                      p-5
                      text-sm
                      text-black
                      outline-none
                      [&_a]:text-blue-600
                      [&_a]:underline
                      [&_ol]:list-decimal
                      [&_ol]:pl-6
                      [&_p]:my-2
                      [&_ul]:list-disc
                      [&_ul]:pl-6
                    "
                  />
                </div>

                <p className="text-xs text-muted-foreground">
                  Edit the email directly as it
                  will appear to the customer.
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0">
          <Button
            variant="outline"
            onClick={() =>
              onOpenChange(false)
            }
          >
            Cancel
          </Button>

          <Button
            onClick={() =>
              void handleSend()
            }
            disabled={
  loading ||
  trackingEmail.loading
}
            className="gap-1.5"
          >
            {trackingEmail.loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Mail className="h-4 w-4" />
            )}

            Send Email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function EditorButton({
  title,
  onClick,
  children,
}: {
  title: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={event => {
        /*
         * Prevent the button from stealing the
         * editor's current text selection.
         */
        event.preventDefault()
      }}
      onClick={onClick}
      className="
        inline-flex
        h-8
        w-8
        items-center
        justify-center
        rounded-md
        text-muted-foreground
        transition-colors
        hover:bg-accent
        hover:text-accent-foreground
      "
    >
      {children}
    </button>
  )
}